import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api, formatUsd, formatUptime, type Instance, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button, Card, StatusPill, Modal, Alert, Empty, Spinner } from '../components/ui';

export function Machines() {
  const { can, user } = useAuth();
  const [instances, setInstances] = useState<Instance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showUnmanaged, setShowUnmanaged] = useState(false);
  const [terminating, setTerminating] = useState<Instance | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const data = await api.get<{ instances: Instance[] }>('/instances');
      setInstances(data.instances);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load instances.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    // 15s refresh: fast enough that a start/stop feels responsive, slow enough
    // not to hammer the API from a screen left open all day.
    const timer = setInterval(() => void load(), 15_000);
    return () => clearInterval(timer);
  }, [load]);

  async function act(instance: Instance, action: string, body?: unknown): Promise<void> {
    setBusyId(instance.instanceId);
    setError(null);

    try {
      const r = await api.post<{ message?: string }>(`/instances/${instance.instanceId}/${action}`, body);
      await load();
      setNotice(r?.message ?? null);
      setTerminating(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `Could not ${action} the instance.`);
    } finally {
      setBusyId(null);
    }
  }

  const managed = instances.filter((i) => i.managed);
  const unmanaged = instances.filter((i) => !i.managed);
  const visible = showUnmanaged ? instances : managed;

  if (loading) return <Spinner label="Loading instances…" />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Instances</h1>
          <p className="text-sm text-ink-muted">
            {managed.length} managed
            {unmanaged.length > 0 && `, ${unmanaged.length} other in this account`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {user?.isAdmin && (
            <Link to="/adopted" className="text-xs text-ink-secondary hover:underline">Adopted resources</Link>
          )}
          {can('resource:create') && (
            <Link to="/launch"><Button variant="primary">Launch instance</Button></Link>
          )}
          {unmanaged.length > 0 && (
            <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-secondary">
              <input
                type="checkbox"
                checked={showUnmanaged}
                onChange={(e) => setShowUnmanaged(e.target.checked)}
              />
              Show unmanaged
            </label>
          )}
          <Button
            onClick={async () => {
              setLoading(true);
              await api.post('/instances/refresh').catch(() => undefined);
              await load();
            }}
          >
            Refresh
          </Button>
        </div>
      </div>

      {error && <Alert tone="critical">{error}</Alert>}
      {notice && <Alert tone="warning">{notice}</Alert>}

      <Card>
        {visible.length === 0 ? (
          <Empty title="No instances yet">
            {can('resource:create') ? (
              <>
                Press <Link to="/launch" className="underline">Launch instance</Link> to start one from a template.
              </>
            ) : (
              'Ask a producer to launch one for you.'
            )}
          </Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-grid text-left text-xs text-ink-muted">
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="hidden md:table-cell px-4 py-2 font-medium">Type</th>
                  <th className="hidden md:table-cell px-4 py-2 font-medium">Region</th>
                  <th className="hidden md:table-cell px-4 py-2 font-medium">Uptime</th>
                  <th className="hidden md:table-cell px-4 py-2 text-right font-medium">Rate</th>
                  <th className="px-4 py-2 text-right font-medium">This session</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {visible.map((instance) => {
                  const busy = busyId === instance.instanceId || !!instance.pendingOperation;

                  return (
                    <tr
                      key={`${instance.region}-${instance.instanceId}`}
                      className={`border-b border-grid last:border-0 hover:bg-raised/40 ${
                        instance.managed ? '' : 'opacity-55'
                      }`}
                    >
                      <td className="px-4 py-2.5">
                        <Link
                          to={`/machines/${instance.instanceId}`}
                          className="font-medium hover:underline"
                        >
                          {instance.name ?? instance.instanceId}
                        </Link>
                        <div className="font-mono text-[11px] text-ink-muted">
                          {instance.instanceId}
                        </div>
                      </td>

                      <td className="px-4 py-2.5">
                        <StatusPill state={instance.state} pending={instance.pendingOperation} />
                      </td>

                      <td className="hidden md:table-cell px-4 py-2.5 text-ink-secondary">
                        {instance.instanceType ?? '—'}
                      </td>

                      <td className="hidden md:table-cell px-4 py-2.5 text-xs text-ink-secondary">
                        {instance.region}
                      </td>

                      <td className="hidden md:table-cell px-4 py-2.5 text-xs tabular text-ink-secondary">
                        {instance.state === 'running' ? formatUptime(instance.launchTime) : '—'}
                      </td>

                      <td className="hidden md:table-cell px-4 py-2.5 text-right text-xs tabular text-ink-secondary">
                        {instance.usdPerHour ? `${formatUsd(instance.usdPerHour, 3)}/hr` : '—'}
                      </td>

                      <td className="px-4 py-2.5 text-right text-xs tabular">
                        {instance.state === 'running' && instance.currentSessionUsd !== null ? (
                          <span className="text-ink">
                            {formatUsd(instance.currentSessionUsd)}
                          </span>
                        ) : (
                          <span className="text-ink-muted">—</span>
                        )}
                      </td>

                      <td className="px-4 py-2.5">
                        {instance.managed ? (
                          <div className="flex justify-end gap-1">
                            {instance.state === 'stopped' && can('resource:start') && (
                              <Button
                                variant="primary"
                                loading={busy}
                                onClick={() => void act(instance, 'start')}
                              >
                                Start
                              </Button>
                            )}
                            {instance.state === 'running' && can('resource:stop') && (
                              <Button loading={busy} onClick={() => void act(instance, 'stop')}>
                                Stop
                              </Button>
                            )}
                            {can('resource:terminate') && instance.state !== 'terminated' && (
                              <Button
                                variant="ghost"
                                disabled={busy}
                                onClick={() => {
                                  setTerminating(instance);
                                }}
                              >
                                Terminate
                              </Button>
                            )}
                          </div>
                        ) : instance.adopted ? (
                          // No controls here on purpose: an adopted machine is
                          // started and stopped from its own page and nowhere else.
                          <Link
                            to="/adopted"
                            className="block text-right text-[11px] text-ink-muted hover:underline"
                            title="Built outside the hub and adopted. It can be started and stopped from Adopted resources; the hub never changes or terminates it."
                          >
                            adopted - protected
                          </Link>
                        ) : (
                          <span
                            className="block text-right text-[11px] text-ink-muted"
                            title="Not created by this dashboard, so it cannot be controlled from here. This protects pre-existing instances."
                          >
                            unmanaged
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Terminate asks plainly, with what is lost spelled out. The server still
          wants the name, which the page supplies. */}
      <Modal
        open={!!terminating}
        onClose={() => setTerminating(null)}
        title={`Terminate ${terminating?.name ?? terminating?.instanceId ?? 'machine'}?`}
        footer={
          <>
            <Button onClick={() => setTerminating(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={busyId === terminating?.instanceId}
              onClick={() =>
                terminating && void act(terminating, 'terminate', { confirmName: terminating.name ?? terminating.instanceId })
              }
            >
              Terminate permanently
            </Button>
          </>
        }
      >
        {terminating && (
          <div className="space-y-3">
            <Alert tone="critical" title="This cannot be undone">
              The instance and its storage volume are destroyed: every file on it, every setting, and its
              addresses. Anything not saved to the shared bucket or captured as an AMI is lost.
            </Alert>

            <p className="text-sm text-ink-secondary">
              If you only want to stop paying for compute, <strong>stop</strong> it instead. A
              stopped instance keeps everything on it and costs only storage — roughly $1.60 a day
              for a 500&nbsp;GB volume, against{' '}
              {formatUsd((terminating.usdPerHour ?? 0) * 24)} a day running.
            </p>

          </div>
        )}
      </Modal>
    </div>
  );
}
